/**
 * Der Personen-Dialog: Anzeigename, ERP-Identität, Rolle, aktiv-Kennzeichen.
 *
 * **Personen entstehen bei der ersten Anmeldung**, nicht hier — angelegt wird in
 * dieser Maske niemand. Vergeben wird die Rolle, und sie ist das eine, was in
 * diesem System und nicht im SilverERP entschieden wird: Das ERP liefert die
 * Identität, an die die Rolle gebunden ist.
 *
 * Der letzte Administrator kann sich die eigene Rolle nicht entziehen. Diese
 * Regel steht im Server; hier steht nur, was er dazu sagt.
 *
 * **Blindes Begutachten steht daneben und nicht in der Rolle** (ADR-0013): Die
 * Rollen beschreiben Berechtigungsstufen, das Blindarbeiten ist eine dazu
 * senkrechte Betriebsart — ein `management`, der blind arbeitet, muss möglich
 * sein. Deshalb ein eigenes Feld und ein eigener Weg im Server, der die Rolle
 * nicht anfasst.
 *
 * **Die Chat-Kennung steht hier und nirgends sonst** (ADR-0027; #103): Wer Rollen
 * vergibt, pflegt sie und muss sie deshalb sehen. Beschriftet ist sie mit dem
 * Hersteller — in der Oberfläche steht „Mattermost", weil ein Mensch wissen will,
 * wo seine Nachricht landet; im Code steht er nicht.
 *
 * **Fehlt der Zugang zum Chat, sagt der Abschnitt es** (#104): Die Kennung lässt
 * sich weiter pflegen, aber es geht keine Nachricht hinaus. Eine Maske, die
 * gepflegt aussieht und trotzdem nichts bewirkt, ist die unangenehmste Art, eine
 * fehlende Einstellung zu erfahren — dieselbe Begründung wie beim Abschnitt
 * „Ticketsystem" und derselbe Satzbau.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  Dialog,
  FormGrid,
  FormRow,
  Modal,
  Select,
  StatusPill,
  TextField,
} from "../../design-system";
import type { AdminApi, Person, Role } from "../../api/client";
import { StrongCell } from "../cells";
import { ListSection } from "../list";
import { Notice, describe, useLoaded } from "../loading";

/** Die drei Rollen, in der Reihenfolge, in der sie mehr dürfen. */
const ROLES: Role[] = ["dev", "management", "admin"];

export function PeopleSection({ client }: { client: AdminApi }): ReactElement {
  const { state, reload } = useLoaded(() => client.people());
  const [edited, setEdited] = useState<Person | null>(null);
  const people = state.kind === "loaded" ? state.value.people : [];

  return (
    <>
      <ListSection
        title="Personen"
        glyph="users"
        first
        headerRight={
          state.kind === "loaded" ? <Chip>{`${people.length} angemeldet`}</Chip> : null
        }
      >
        <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {state.kind === "loaded" && people.length === 0 ? (
            <Notice text="Noch hat sich niemand angemeldet." />
          ) : null}
          {state.kind === "loaded" && !state.value.mattermostConfigured ? (
            // Ohne Auszeichnung: Die Schlüsselnamen stehen im Fließtext, denn
            // diese Oberfläche kennt nur eine Schrift.
            <Notice
              glyph="circle-alert"
              color="var(--ac-amber)"
              text={
                "Es ist kein Zugang zu Mattermost eingestellt: Eine Mattermost-Kennung lässt sich "
                + "hier pflegen, aber es geht keine Direktnachricht hinaus — der Übergabe-Dialog "
                + "zeigt dann keinen Mattermost-Knopf. Der Zugang steht als "
                + "mattermost_base_address und mattermost_bot_token in config/configuration.php "
                + "und lässt sich hier nicht setzen."
              }
            />
          ) : null}
        </div>

        {state.kind === "loaded" && people.length > 0 ? (
          <DataTable
            onRowClick={(_row, index) => setEdited(people[index] ?? null)}
            columns={[
              { key: "name", label: "Anzeigename", width: 240, maxWidth: 240 },
              { key: "identity", label: "ERP-Identität", width: 140 },
              { key: "svn", label: "SVN-Kennung", width: 220 },
              { key: "chat", label: "Mattermost-Kennung", width: 200 },
              { key: "role", label: "Rolle", width: 140 },
              { key: "blind", label: "Begutachtung", width: 140 },
              { key: "state", label: "Zustand", width: 110 },
            ]}
            rows={people.map((person) => ({
              cells: {
                name: <StrongCell>{person.displayName}</StrongCell>,
                identity: person.erpIdentity,
                svn: <IdentityChips values={person.svnIdentities} />,
                chat:
                  person.chatHandle === null ? <NoneLabel /> : <Chip>{person.chatHandle}</Chip>,
                /*
                  Nur `admin` trägt eine Farbe: Die Rolle, die alles darf, soll in
                  der Liste auffallen. `dev` und `management` sind kein Zustand,
                  sondern eine Angabe, und stehen deshalb farblos da.
                */
                role:
                  person.role === "admin" ? (
                    <StatusPill tone="blue">{person.role}</StatusPill>
                  ) : (
                    <Chip>{person.role}</Chip>
                  ),
                blind: person.blindReview ? (
                  <StatusPill tone="blue">blind</StatusPill>
                ) : (
                  <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>offen</span>
                ),
                state: person.active ? (
                  <StatusPill tone="green">aktiv</StatusPill>
                ) : (
                  <Chip>stillgelegt</Chip>
                ),
              },
            }))}
          />
        ) : null}
      </ListSection>

      {edited === null ? null : (
        <PersonDialog
          person={edited}
          save={async (role, blindReview, chatHandle) => {
            // Drei Aufrufe, weil es drei Achsen sind — und nur der, an dem sich
            // etwas geändert hat: Ein Rollenwechsel, den niemand wollte, träfe
            // sonst die Regel über den letzten Administrator.
            if (role !== edited.role) {
              await client.setRole(edited.id, role);
            }

            if (blindReview !== edited.blindReview) {
              await client.setBlindReview(edited.id, blindReview);
            }

            if (chatHandle !== edited.chatHandle) {
              await client.setChatHandle(edited.id, chatHandle);
            }
          }}
          close={() => setEdited(null)}
          saved={() => {
            setEdited(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function PersonDialog({
  person,
  save,
  close,
  saved,
}: {
  person: Person;
  save: (role: Role, blindReview: boolean, chatHandle: string | null) => Promise<void>;
  close: () => void;
  saved: () => void;
}): ReactElement {
  const [role, setRole] = useState<Role>(person.role);
  const [blindReview, setBlindReview] = useState(person.blindReview);
  const [chatHandle, setChatHandle] = useState(person.chatHandle ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    setError(null);

    // Ein leeres Feld heißt „keine" und nicht „leerer Text". Getrimmt wird hier
    // nur für diese Unterscheidung; das führende `@` schneidet der Server ab,
    // damit die Regel an einer Stelle steht.
    const typed = chatHandle.trim();

    save(role, blindReview, typed === "" ? null : typed).then(saved, (failure: unknown) => {
      setBusy(false);
      setError(describe(failure));
    });
  };

  return (
    <Modal onClose={close}>
      <Dialog
        title="Rolle, Begutachtung und Mattermost-Kennung"
        description={`${person.displayName}, ERP-Identität ${person.erpIdentity}. Alles drei wird lokal geführt und an diese Identität gebunden; das ERP liefert die Identität, nicht die Rolle.`}
        width={520}
        onClose={close}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={close}>
              Abbrechen
            </Button>
            <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
              {busy ? "Wird gespeichert ..." : "Speichern"}
            </Button>
          </>
        }
      >
        <FormGrid labelWidth="var(--grid-label-col)">
          <FormRow label="Rolle">
            <Select
              options={ROLES}
              value={role}
              onSelect={(chosen) => setRole(isRole(chosen) ? chosen : role)}
            />
          </FormRow>

          <FormRow label="">
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              dev begutachtet, management schärft die Regeln, admin verwaltet Endpunkte, Agenten,
              Projekte und Rollen.
            </span>
          </FormRow>

          <FormRow label="Begutachtung">
            <Checkbox
              label="blind begutachten"
              checked={blindReview}
              onChange={setBlindReview}
            />
          </FormRow>

          <FormRow label="">
            <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
              Wer blind begutachtet, sieht bis zu seiner Abgabe keine fremden Befunde — weder die
              der Agenten noch die der Kollegen. Vorbelegt ist das aus: Was gefunden wird, soll
              benutzt werden. Die Einstellung steht senkrecht zur Rolle.
            </span>
          </FormRow>

          <FormRow label="Mattermost-Kennung" htmlFor="person-chat-handle">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
              <TextField
                id="person-chat-handle"
                value={chatHandle}
                onChange={setChatHandle}
                placeholder="mmustermann"
              />
              <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
                Sie ist das Ziel, wenn ein Text als Direktnachricht an diese Person geht — ohne sie
                ist sie so nicht erreichbar, und geraten wird keine Adresse. Ein führendes @ wird
                beim Speichern abgeschnitten, leer lassen entfernt die Kennung. Ob es sie in
                Mattermost gibt, prüft diese Maske nicht: Das weiß nur Mattermost.
              </span>
            </div>
          </FormRow>

          {error === null ? null : (
            <FormRow full>
              <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
            </FormRow>
          )}
        </FormGrid>
      </Dialog>
    </Modal>
  );
}

function isRole(value: string): value is Role {
  return value === "dev" || value === "management" || value === "admin";
}

/**
 * „keine", gedämpft, in derselben Form wie die Spalte „Begutachtung" für ein
 * offenes Begutachten: keine leere Zelle, denn die läse sich wie ein
 * Ladefehler, und keine Pille, denn eine Pille steht hier für einen Eintrag und
 * ein fehlender ist keiner.
 */
function NoneLabel(): ReactElement {
  return <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>keine</span>;
}

/**
 * Die zugeordneten SVN-Kennungen einer Person, je eine als eigener `Chip`: Eine
 * Person kann über die Zeit mehrere haben, und ausgezählt werden können sie
 * nur, wenn jede für sich steht. Farblos, denn eine Kennung ist eine Kennung
 * und keine Auszeichnung — das ist der Fall, für den es `core/Chip` gibt.
 */
function IdentityChips({ values }: { values: string[] }): ReactElement {
  if (values.length === 0) {
    return <NoneLabel />;
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-2)" }}>
      {values.map((value) => (
        <Chip key={value}>
          {value}
        </Chip>
      ))}
    </div>
  );
}
